import { ReportHighlights } from '@data/components/report/ReportHighlights/ReportHighlights';
import { resolveMetrics } from '@data/utils/resolve-metrics';
import {
  OVERVIEW_PRIMARY_METRICS,
  OVERVIEW_SECONDARY_METRICS,
  OVERVIEW_TABLE_COLUMNS,
} from '@data/config/overview-metric';
import { Table } from '@data/components/basic/Table/Table';
import { Drilldown } from '@data/main';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetMeApi } from 'eds-common/api/v2';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import {
  MetricTableType,
  useMetricsTableV2Api,
} from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { buildOrderBy } from '@data/utils/build-order-by';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { filterTableData } from '@data/utils/filter-table-data';
import { useResetTableOnFilterChange } from '@data/hooks/use-report-tables/use-reset-table-on-filter-change/use-reset-table-on-filter-change';
import {
  NON_MAGAZINE_PRODUCTS as NON_MAGAZINE_ED_APPS,
  type ProductCode,
} from '@data/config/reports';
import { useExportToCSVHandler } from '@data/hooks/use-report-tables/use-export-to-csv-handler/use-export-to-csv-handler';
import { getEdApps } from '@data/utils/get-ed-apps';
import {
  useEffectiveClassIds,
  useEffectiveStudentIds,
} from '@data/hooks/use-effective-filters/use-effective-filters';

// ── Hooks ───────────────────────────────────────────────────────────────────────

function getTableMetricType(drilldown: Drilldown) {
  if (drilldown === Drilldown.Class) {
    return MetricTableType.IndividualClassOverview;
  }
  return MetricTableType.AllClassOverview;
}

function useDrilldownHandler(
  drilldown: Drilldown,
  edApp: ProductCode,
  firstClassId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
  dateParams: string
) {
  return useCallback(
    (id: string | number) => {
      // Carry the date range on drill-down only when a non-default range is
      // active — it is mirrored on the URL as `from`/`to`. On the default
      // "Current school year" the URL has no date params, so the drill-down
      // page opens on the school-year default instead of flipping the date
      // dropdown to "Custom range".
      const suffix = dateParams ? `&${dateParams}` : '';
      if (drilldown === Drilldown.AllClasses) {
        navigate(`/data/${edApp}/overview/class?classId=${id}${suffix}`);
      }
      if (drilldown === Drilldown.Class && firstClassId) {
        navigate(
          `/data/${edApp}/overview/student?classId=${firstClassId}&studentId=${id}${suffix}`
        );
      }
    },
    [drilldown, firstClassId, navigate, edApp, dateParams]
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export function OverviewPage() {
  const navigate = useNavigate();
  const {
    dateRange,
    drilldown,
    productId: edApp,
    reportPath,
  } = useReportOutletContext();
  const { data: user } = useGetMeApi();
  const { data: applications } = useApplications();
  const [searchParams] = useSearchParams();
  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  const firstClassId = classIds[0];
  // Only a non-default range is mirrored on the URL as `from`/`to`; forward it
  // verbatim on drill-down and carry nothing on the school-year default.
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const dateParams =
    fromParam && toParam ? `from=${fromParam}&to=${toParam}` : '';

  // Extract user's product codes for the current application
  const userProductCodes = applications
    ?.find((app) => app.applicationCode === edApp)
    ?.products.map((p) => p.productCode);

  // ── Metrics ──────────────────────────────────────────────────────────────────

  const primaryMetricTypes = resolveMetrics(
    OVERVIEW_PRIMARY_METRICS,
    edApp,
    drilldown,
    userProductCodes
  );

  const secondaryMetricTypes =
    (NON_MAGAZINE_ED_APPS as string[]).includes(edApp) && edApp !== 'rise'
      ? resolveMetrics(
          OVERVIEW_SECONDARY_METRICS,
          edApp,
          drilldown,
          userProductCodes
        )
      : [];

  // ── Table ──────────────────────────────────────────────────────────────────

  const showTable =
    (NON_MAGAZINE_ED_APPS as string[]).includes(edApp) &&
    (drilldown === Drilldown.AllClasses || drilldown === Drilldown.Class);

  const tableMetricType = useMemo(
    () => getTableMetricType(drilldown),
    [drilldown]
  );

  const defaultSortColumn = drilldown === Drilldown.Class ? 'STUDENT' : 'CLASS';

  const { tableProps, searchTerms, userSorted, reset } = useTableControls({
    defaultSortColumn,
  });

  useResetTableOnFilterChange(
    drilldown,
    classIds.join(','),
    studentIds.join(','),
    reset
  );

  const effectiveClassIds = useEffectiveClassIds({
    orgId: user?.currentOrg?.orgId ?? '',
    userId: user?.userId ?? '',
    edApp,
    selectedClassIds: classIds,
  });
  const { studentIds: effectiveStudentIds, isReady: studentsReady } =
    useEffectiveStudentIds({
      drilldown,
      classIds,
      edApp,
      selectedStudentIds: studentIds,
    });

  const sharedMetricsArgs = {
    orgId: user?.currentOrg?.orgId ?? '',
    metricType: tableMetricType,
    teacherIds: [user?.userId ?? ''],
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(edApp),
    classIds: effectiveClassIds,
    studentIds: effectiveStudentIds,
    pageSize: tableProps.pageSize,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
  };

  const {
    data: tableData,
    loading: tableMetricsLoading,
    error,
  } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: tableProps.page,
    enabled:
      showTable &&
      !!user?.userId &&
      !!user?.currentOrg?.orgId &&
      !!edApp &&
      effectiveClassIds.length > 0 &&
      studentsReady,
  });

  const { refetch: refetchExportData } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const { visibleColumns, visibleRows } = useMemo(
    () => filterTableData(tableData, OVERVIEW_TABLE_COLUMNS, edApp),
    [tableData, edApp]
  );

  const handleExportToCSV = useExportToCSVHandler(
    refetchExportData,
    OVERVIEW_TABLE_COLUMNS,
    edApp,
    reportPath
  );

  const handleDrilldown = useDrilldownHandler(
    drilldown,
    edApp,
    firstClassId,
    navigate,
    dateParams
  );

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <ReportHighlights
        primaryMetricTypes={primaryMetricTypes}
        secondaryMetricTypes={secondaryMetricTypes}
      />
      {showTable && (
        <>
          <span className="text-xl font-bold text-neutral-1300 tablet:text-2xl">
            Details
          </span>
          <Table
            {...tableProps}
            loading={tableMetricsLoading}
            columns={visibleColumns}
            rows={visibleRows}
            totalCount={tableData?.totalRecords ?? 0}
            onExportToCSV={handleExportToCSV}
            onDrilldown={handleDrilldown}
            error={error}
          />
        </>
      )}
    </div>
  );
}
